/**
 * /clinic: register if this account belongs to no clinic, choose one if it belongs to several and
 * this session has not chosen, record a licence if an invited practitioner still needs one,
 * otherwise the clinic home. A Lattice staff account in no clinic is pointed to the admin app,
 * because the Worker refuses to let staff register a clinic.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { ClinicChoice, ClinicHome, ClinicSnapshot } from "@lattice/core";
import { clinicRegistrationRefusal } from "@lattice/core";
import { chooseClinic, loadClinic, loadSession, signOut } from "../api.ts";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "@lattice/ui/Button";
import { ErrorState } from "@lattice/ui/ErrorState";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { LicenceForm } from "./LicenceForm.tsx";
import { Portal } from "./Portal.tsx";
import { Register } from "./Register.tsx";
import { ClinicShell, clinicText } from "./Shell.tsx";
import { ClinicSignIn } from "./SignIn.tsx";
import { StepUpDialog } from "../security/StepUp.tsx";
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "sign-in";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "register";
} | {
  readonly kind: "staff";
} | {
  readonly kind: "choose";
  readonly clinics: readonly ClinicChoice[];
} | {
  readonly kind: "licence";
  readonly clinics: readonly ClinicChoice[];
} | {
  readonly kind: "home";
  readonly clinic: ClinicSnapshot;
  readonly clinics: readonly ClinicChoice[];
};
function screenOf({
  clinic,
  clinics
}: ClinicHome): Screen {
  if (clinic === null) return clinics.length === 0 ? {
    kind: "register"
  } : {
    kind: "choose",
    clinics
  };
  if (clinic.roles.includes("practitioner") && clinic.licence === null) return {
    kind: "licence",
    clinics
  };
  return {
    kind: "home",
    clinic,
    clinics
  };
}

/* /api/me is asked only for a home with no clinic, the one case where staff see a different screen. */
async function openingScreen(): Promise<Screen> {
  const loaded = await loadClinic();
  if (loaded.status === "signed_out") return {
    kind: "sign-in"
  };
  if (loaded.status !== "ok") return {
    kind: "failed"
  };
  const screen = screenOf(loaded.home);
  if (screen.kind !== "register") return screen;
  const session = await loadSession();
  return session.status === "ok" && clinicRegistrationRefusal(session.me) === "staff_account" ? {
    kind: "staff"
  } : screen;
}
const staffStyles = {
  lead: {
    ks0D6T: "x1mrxg6j",
    kN2L0X: "x1w2vvpw",
    $$css: true
  }
};

/*
  A staff account is told where it belongs and left to go, not redirected: someone signed in to the
  wrong account needs the Sign out beside the link, and a redirect would take that away.
*/
function StaffNotice() {
  return <ClinicShell title="Staff accounts use the admin app" centred>
      <p {...stylex.props(clinicText.lead, staffStyles.lead)}>
        This is the clinic portal. Staff accounts can't register or run a clinic, so your work is in
        the admin app.
      </p>
      <div className="x78zum5 x1a02dak xl56j7k x626il7 xh8yej3">
        <LinkButton href="/admin/" fullWidth="phone">
          Open the admin app
        </LinkButton>
        <Button tone="quiet" corners="rounded" fullWidth="phone" onClick={() => {
        void signOut().then(() => window.location.assign("/practitioners"));
      }}>
          Sign out
        </Button>
      </div>
    </ClinicShell>;
}
function Choose({
  clinics,
  onChosen
}: {
  readonly clinics: readonly ClinicChoice[];
  readonly onChosen: (home: ClinicHome) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  return <ClinicShell title="Choose a clinic" description={`You belong to ${String(clinics.length)} clinics. You can switch later from the clinic home.`} signedIn>
      {clinics.map(choice => <Button key={choice.id} tone="quiet" corners="rounded" busy={busy === choice.id} onClick={() => {
      setBusy(choice.id);
      setProblem(null);
      void chooseClinic(choice.id).then(loaded => {
        setBusy(null);
        if (loaded.status === "ok") onChosen(loaded.home);else setProblem("Try again.");
      }, () => {
        setBusy(null);
        setProblem("Check your connection and try again.");
      });
    }}>
          {choice.name}
        </Button>)}
      {problem === null ? null : <ErrorState title="We couldn't open that clinic" body={problem} />}
    </ClinicShell>;
}
function SignedInScreen({
  screen,
  setScreen
}: {
  readonly screen: Exclude<Screen, {
    readonly kind: "loading" | "sign-in" | "failed" | "staff";
  }>;
  readonly setScreen: (screen: Screen) => void;
}) {
  if (screen.kind === "register") return <Register onRegistered={() => window.location.assign("/clinic")} />;
  if (screen.kind === "choose") return <Choose clinics={screen.clinics} onChosen={home => setScreen(screenOf(home))} />;
  if (screen.kind === "licence") return <LicenceForm onSaved={clinic => setScreen(screenOf({
    clinic,
    clinics: screen.clinics
  }))} />;
  return <Portal clinic={screen.clinic} clinics={screen.clinics} onChange={clinic => setScreen(screenOf({
    clinic,
    clinics: screen.clinics
  }))} onSwitch={home => setScreen(screenOf(home))} />;
}
export function ClinicApp() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  useEffect(() => {
    void openingScreen().then(setScreen, () => setScreen({
      kind: "failed"
    }));
  }, []);
  if (screen.kind === "sign-in") return <ClinicSignIn />;
  if (screen.kind === "loading") return <ClinicShell title="Clinic" signedIn>
        <Pending message="Loading the clinic" bones={["title", "line", "line"]} />
      </ClinicShell>;
  if (screen.kind === "failed") return <ClinicShell title="Clinic" signedIn>
        <ErrorState variant="page" title="We couldn't load the clinic" body="Try again in a minute. If it keeps happening, sign in again." onRetry={() => window.location.reload()} />
        <LinkButton href="/practitioners" tone="quiet">
          Go to sign-in
        </LinkButton>
      </ClinicShell>;

  /* The clinic navigation leads to screens a staff account cannot use, so the notice has none. */
  if (screen.kind === "staff") return <StaffNotice />;
  return <>
      <SignedInScreen screen={screen} setScreen={setScreen} />
      <StepUpDialog />
    </>;
}